import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createStore } from 'redux';
import rootReducer from './redux/reducers';
import { UserRoleProvider } from './context/UserRoleContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './loggers/ErrorBoundary';
import errorLogger from './utils/errorLogger';
import AuthCheck from './loggers/AuthCheck';

import SplashScreen from './pages/main/SplashScreen';
import LandingPage from './pages/main/LandingPage';
import TermsOfService from './pages/main/TermsOfService';
import PrivacyPolicy from './pages/main/PrivacyPolicy';
import CookiePolicy from './pages/main/CookiePolicy';
import AccountNotActivatedPage from './pages/AccountNotActivatedPage';
import LegalConsentGate from './components/TermsAndConditions/LegalConsentGate';

import ModeratorDashboard from './pages/users/moderator/ModeratorDashboard';
import ClientDashboard from './pages/users/client/ClientDashboard';
import AluminiDashboard from './pages/users/alumini/AluminiDashboard';
import AdminDashboard from './pages/users/admin/AdminDashboard';
import CoachesDashboard from './pages/users/coachesmentors/CoachesDashboard';
import SuperAdminDash from './pages/users/superadmin/SuperAdminDash';
import EntrepreneurProfilingDashboard from './components/ProfilingForms/EntrepreneurProfilingDashboard';
import ProfilingAnalyticsDashboard from './components/ProfilingForms/ProfilingAnalyticsDashboard';
import PlatformEngineerDashboard from './pages/users/platformengineer/DevDashboard';
import TeamMemberLogin from './components/team/TeamMemberLogin';
import TeamMemberDashboard from './components/team/TeamMemberDashboard';
import TeamMembersDemo from './components/team/TeamMembersDemo';
import TestCompanyProfile from './components/team/TestCompanyProfile';

import ProtectedRoute from './protected/ProtectedRoute';
import TeamMemberProtectedRoute from './protected/TeamMemberProtectedRoute';

import ClientOtpPrompt from './components/auth/ClientOtpPrompt';

import { ToastContainer, toast } from 'react-toastify';
import { ToastProvider as LegacyToastProvider } from './hooks/use-toast';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import 'react-toastify/dist/ReactToastify.css';
import './styles/global.css';
import './App.css';

import GuidedAssistant from './components/GuidedAssistant/GuidedAssistant';

// Clean URL hook (keeps history routing functional)
const HideUrlPaths = ({ children }) => {
  const location = useLocation();
  useEffect(() => {
    const baseUrl = window.location.origin;
    const path = location.pathname;
    const state = { ...window.history.state, as: path, path };

    if (window.location.pathname !== '/') {
      window.history.replaceState(state, '', baseUrl);
    }

    const handlePop = e => {
      if (e.state?.path) {
        setTimeout(() => {
          window.history.replaceState(state, '', baseUrl);
        }, 0);
      }
    };

    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [location.pathname]);

  return <>{children}</>;
};

const store = createStore(rootReducer);

// Helper to show GuidedAssistant only when authenticated
const WithGuidedAssistant = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return children;
  return (
    <>
      {children}
      <GuidedAssistant />
    </>
  );
};

function App() {
  // User activity tracking for error context
  useEffect(() => {
    const markInteraction = () => {
      try {
        if (errorLogger?.setUserInteracted) errorLogger.setUserInteracted();
        else window.userHasInteracted = true;
      } catch {
        window.userHasInteracted = true;
      } finally {
        document.removeEventListener('click', markInteraction);
        document.removeEventListener('keydown', markInteraction);
        document.removeEventListener('touchstart', markInteraction);
      }
    };

    document.addEventListener('click', markInteraction);
    document.addEventListener('keydown', markInteraction);
    document.addEventListener('touchstart', markInteraction);
    return () => {
      document.removeEventListener('click', markInteraction);
      document.removeEventListener('keydown', markInteraction);
      document.removeEventListener('touchstart', markInteraction);
    };
  }, []);

  // Global error handling
  useEffect(() => {
    const handleRejection = e => {
      if (
        e.reason?.message?.includes('Could not establish connection') ||
        e.reason?.message?.includes('Receiving end does not exist')
      ) {
        e.preventDefault();
        return toast.warn('Browser extension issue detected (non-critical).');
      }
      toast.error(`Unhandled rejection: ${e.reason?.message || e.reason}`);
    };

    const handleError = e => {
      if (
        e.error?.message?.includes('Could not establish connection') ||
        e.error?.message?.includes('Receiving end does not exist')
      ) {
        e.preventDefault();
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <ToastProvider>
            <LegacyToastProvider>
              <AuthProvider>
                <UserRoleProvider>
                  <Router>
                    <HideUrlPaths>
                      <AuthCheck>
                        <LegalConsentGate />
                        <ToastContainer position="bottom-right" autoClose={3500} theme="colored" />

                        <Routes>
                          <Route path="/" element={<SplashScreen />} />
                          <Route path="/landing" element={<LandingPage />} />
                          <Route
                            path="/account-not-activated"
                            element={<AccountNotActivatedPage />}
                          />
                          <Route path="/terms" element={<TermsOfService />} />
                          <Route path="/privacy" element={<PrivacyPolicy />} />
                          <Route path="/cookies" element={<CookiePolicy />} />

                          {/* Protected User Routes */}
                          <Route
                            path="/moderator"
                            element={
                              <ProtectedRoute allowedRoles={['moderator', 'Business Advisor']}>
                                <ModeratorDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/business-advisor"
                            element={
                              <ProtectedRoute allowedRoles={['Business Advisor']}>
                                <ModeratorDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/client"
                            element={
                              <ProtectedRoute allowedRoles={['client']}>
                                <ClientDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/alumini"
                            element={
                              <ProtectedRoute allowedRoles={['alumni']}>
                                <AluminiDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <ProtectedRoute allowedRoles={['admin', 'admins']}>
                                <AdminDashboard />
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/PlatformEngineer/*"
                            element={
                              <ProtectedRoute allowedRoles={['PlatformEngineer']}>
                                <PlatformEngineerDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/coach"
                            element={
                              <ProtectedRoute
                                allowedRoles={[
                                  'coach',
                                  'coaches',
                                  'businessCoach',
                                  'businessCoaches',
                                ]}
                              >
                                <CoachesDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/super-admin"
                            element={
                              <ProtectedRoute allowedRoles={['superAdmin', 'superAdmins']}>
                                <SuperAdminDash />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/profiling"
                            element={
                              <ProtectedRoute
                                allowedRoles={[
                                  'client',
                                  'entrepreneur',
                                  'coach',
                                  'admin',
                                  'superAdmin',
                                  'PlatformEngineer',
                                ]}
                              >
                                <EntrepreneurProfilingDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/profiling-analytics"
                            element={
                              <ProtectedRoute
                                allowedRoles={[
                                  'coach',
                                  'admin',
                                  'moderator',
                                  'client',
                                  'entrepreneur',
                                  'superAdmin',
                                  'PlatformEngineer',
                                ]}
                              >
                                <ProfilingAnalyticsDashboard />
                              </ProtectedRoute>
                            }
                          />

                          {/* Team Member Routes */}
                          <Route path="/team-login" element={<TeamMemberLogin />} />
                          <Route
                            path="/team-dashboard"
                            element={
                              <TeamMemberProtectedRoute>
                                <TeamMemberDashboard />
                              </TeamMemberProtectedRoute>
                            }
                          />
                          <Route path="/test-company-profile" element={<TestCompanyProfile />} />
                          <Route path="/team-members-demo" element={<TeamMembersDemo />} />
                        </Routes>
                      </AuthCheck>
                    </HideUrlPaths>
                  </Router>
                </UserRoleProvider>
              </AuthProvider>
            </LegacyToastProvider>
          </ToastProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
